import React, { useState } from 'react';

interface Point {
  day: string;
  value: number;
}

/**
 * Barras por día, una sola serie: el título dice qué es, así que no lleva
 * leyenda. Barras finas con el extremo redondeado, cuadrícula discreta y el
 * valor exacto al pasar por encima (la zona sensible es la columna entera,
 * no solo la barra, para que las de valor 0 también se puedan consultar).
 */
export const DailyBars: React.FC<{ title: string; data: Point[]; unit: string }> = ({ title, data, unit }) => {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...data.map((d) => d.value));
  // Tope redondo para el eje: 1, 2, 5, 10, 20, 50…
  const step = [1, 2, 5].map((m) => m * 10 ** Math.floor(Math.log10(max))).find((s) => s * 4 >= max) ?? 10 ** Math.ceil(Math.log10(max));
  const top = Math.max(step * Math.ceil(max / step), 1);
  const ticks = [0, top / 2, top];

  const W = 400;
  const H = 160;
  const padL = 26;
  const padB = 20;
  const innerW = W - padL;
  const innerH = H - padB - 8;
  const col = innerW / data.length;
  const barW = Math.max(3, Math.min(10, col - 3));
  const total = data.reduce((s, d) => s + d.value, 0);
  const h = hover !== null ? data[hover] : null;

  const fmt = (day: string) =>
    new Date(`${day}T12:00:00Z`).toLocaleDateString('es', { day: 'numeric', month: 'short' });

  return (
    <figure className="surface p-4 space-y-2">
      <figcaption className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-semibold text-ink-100">{title}</span>
        <span className="text-xs text-ink-500 tabular-nums">
          {h ? (
            <>
              <span className="text-ink-200 font-semibold">
                {h.value} {unit}
              </span>{' '}
              · {fmt(h.day)}
            </>
          ) : (
            <>
              {total} en 30 días
            </>
          )}
        </span>
      </figcaption>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-40 block"
        role="img"
        aria-label={`${title}: ${data.map((d) => `${fmt(d.day)} ${d.value}`).join(', ')}`}
        onMouseLeave={() => setHover(null)}
      >
        {ticks.map((t) => {
          const y = 8 + innerH - (t / top) * innerH;
          return (
            <g key={t}>
              <line x1={padL} x2={W} y1={y} y2={y} stroke="currentColor" className="text-ink-800" strokeWidth="1" />
              <text x={padL - 6} y={y + 3} textAnchor="end" fontSize="11" className="fill-ink-500">
                {Number.isInteger(t) ? t : t.toFixed(1)}
              </text>
            </g>
          );
        })}
        {data.map((d, i) => {
          const x = padL + i * col + (col - barW) / 2;
          const bh = (d.value / top) * innerH;
          const y = 8 + innerH - bh;
          const active = hover === i;
          return (
            <g key={d.day} onMouseEnter={() => setHover(i)}>
              {/* Zona sensible: la columna entera */}
              <rect x={padL + i * col} y={0} width={col} height={H - padB} fill="transparent" />
              {d.value > 0 && (
                <path
                  d={`M${x} ${8 + innerH} V${y + Math.min(4, bh)} Q${x} ${y} ${x + Math.min(4, barW / 2)} ${y} H${x + barW - Math.min(4, barW / 2)} Q${x + barW} ${y} ${x + barW} ${y + Math.min(4, bh)} V${8 + innerH} Z`}
                  className={active ? 'fill-sky-400' : 'fill-sky-500'}
                />
              )}
              {/* Una fecha por semana contando desde hoy: así la última
                  siempre es hoy y ninguna se pisa con la anterior. */}
              {(data.length - 1 - i) % 7 === 0 && (
                <text
                  x={i === data.length - 1 ? W : padL + i * col + col / 2}
                  y={H - 4}
                  textAnchor={i === data.length - 1 ? 'end' : 'middle'}
                  fontSize="11"
                  className="fill-ink-500"
                >
                  {fmt(d.day)}
                </text>
              )}
            </g>
          );
        })}
        {hover !== null && (
          <line
            x1={padL + hover * col + col / 2}
            x2={padL + hover * col + col / 2}
            y1={8}
            y2={8 + innerH}
            stroke="currentColor"
            className="text-ink-600"
            strokeDasharray="3 3"
            pointerEvents="none"
          />
        )}
      </svg>
    </figure>
  );
};
